import { Data, Model } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { NotificationDuration } from '../../../App/Models/NotificationDuration';
import { AppService, DomainTypes, Types } from '../../DomainTypes';
import Contact from '../../Models/Contacts/Contact';
import ContactLookup from '../../Models/Contacts/ContactLookup';
import ContactCriteria from '../../Models/Contacts/ContactCriteria';

export default class ContactsVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private parentName?: string,
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService),
        public apiClient = AppService.get(DomainTypes.ApiClients.Contact)) {

        super(taskRunner);
        this.makeObservable();
        this.autoDispose(this.criteria.onAnyPropertyChanged(() => this.pageManager.refreshData()))
    }

    public editItem: Contact | null = null;
    public criteria = new ContactCriteria();

    public pageManager = new Data.PageManager(this.criteria, ContactLookup, this.apiClient.getContactsPaged, {
        pageSize: 20,
        initialTaskRunner: this.taskRunner,
        fetchInitial: true,
        allowSort: true,
        sortBy: 'firstName',
    });

    public async editContact(contactId: number | null) {
        if (contactId) {
            const response = await this.taskRunner.waitFor(this.apiClient.getContact(contactId));
            this.editItem = Contact.fromJSObject<Contact>(response.data);
        }
        else {
            this.editItem = new Contact();
        }
    }

    public async saveContact(
        bind: Model.IPropertyInstance<number | null> | undefined,
        bindDisplay: Model.IPropertyInstance<string | null> | undefined,
        onSaved?: (contact?: ContactLookup) => void) {
        
        if (this.editItem) {
            this.taskRunner.run(async () => {
                const result = await this.apiClient.save(this.editItem!.toJSObject());
                if (bind) {
                    bind.value = result.data.contactId;
                }
                if (bindDisplay) {
                    bindDisplay.value = (result.data.values.firstName + " " + result.data.values.lastName).trim();
                }

                if (onSaved) {
                    onSaved(ContactLookup.fromContact(this.editItem!));
                }

                if (this.parentName && this.editItem!.isNew) {
                    this.notifications.addSuccess("Contact created", `Please save the ${this.parentName} to link it to the new contact.`, NotificationDuration.Long);
                } else {
                    this.notifications.addSuccess("Save Successful", "Contact was saved successfully.", NotificationDuration.Standard);
                    this.pageManager.invalidateData();
                }

                this.editItem = null;
            });
        }
    }

    public deleteContact(contactId: number) {
        this.taskRunner.run(async () => {
            await this.apiClient.delete(contactId);
            this.notifications.addSuccess("Delete Successful", "Contact was deleted successfully.", NotificationDuration.Standard);
            this.pageManager.refreshData();
        });
    }
}